import {SyncStatus} from '../offline/entity.js';

export const FEEDBACK_ENTITY_TYPE='feedback_submission';

export const FeedbackKind=Object.freeze({
  BUG:'bug',
  IDEA:'idea'
});

export const FeedbackDeliveryStatus=Object.freeze({
  PENDING:'pending',
  FAILED:'failed'
});

const KINDS=new Set(Object.values(FeedbackKind));
const IMAGE_TYPES=new Set(['image/jpeg','image/png','image/webp']);
export const FEEDBACK_ATTACHMENT_MAX_BYTES=5*1024*1024;
export const FEEDBACK_DIAGNOSTIC_MAX_BYTES=96*1024;

function requiredText(value,label,max){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,max){
  const text=String(value??'').trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError('value is too long');
  return text;
}

function requestId(value){
  const id=requiredText(value,'requestId',80);
  if(!/^feedback_[A-Za-z0-9_.:-]{8,71}$/.test(id)){
    throw new TypeError('requestId must be an opaque feedback identifier');
  }
  return id;
}

function kind(value){
  const key=String(value??'').trim().toLowerCase();
  if(!KINDS.has(key))throw new TypeError('Unsupported feedback kind');
  return key;
}

export function validateFeedbackAttachment(value){
  if(value==null)return null;
  const path=requiredText(value.path,'attachment.path',1024);
  const mimeType=String(value.mimeType??'').trim().toLowerCase();
  if(!IMAGE_TYPES.has(mimeType))throw new TypeError('Unsupported feedback attachment type');
  const sizeBytes=Number(value.sizeBytes);
  if(!Number.isInteger(sizeBytes)||sizeBytes<1||sizeBytes>FEEDBACK_ATTACHMENT_MAX_BYTES){
    throw new TypeError('Feedback attachment size is outside the supported range');
  }
  return Object.freeze({path,mimeType,sizeBytes});
}

export function validateFeedbackDiagnosticBundle(value){
  if(value==null)return null;
  if(!value||typeof value!=='object'||Array.isArray(value)){
    throw new TypeError('diagnosticBundle must be an object');
  }
  const serialized=JSON.stringify(value);
  if(serialized.length>FEEDBACK_DIAGNOSTIC_MAX_BYTES){
    throw new TypeError('diagnosticBundle is too large');
  }
  const schemaVersion=Number(value.schema_version);
  if(schemaVersion!==1||!Array.isArray(value.entries)){
    throw new TypeError('diagnosticBundle has an unsupported schema');
  }
  return Object.freeze(JSON.parse(serialized));
}

export function createFeedbackSubmission({
  requestId:rawRequestId,
  userId,
  kind:rawKind,
  description,
  currentScreen=null,
  currentModule=null,
  diagnosticBundle=null,
  attachment=null,
  createdAt,
  updatedAt=createdAt,
  syncStatus=SyncStatus.PENDING_CREATE,
  syncAttempts=0,
  lastSyncAttemptAt=null,
  lastSyncFailureAt=null,
  lastSyncErrorCode=null,
  nextSyncAttemptAt=null,
  deliveryStatus=FeedbackDeliveryStatus.PENDING
}={}){
  const feedbackKind=kind(rawKind);
  const created=requiredText(createdAt,'createdAt',80);
  const updated=requiredText(updatedAt,'updatedAt',80);
  if(feedbackKind===FeedbackKind.IDEA&&attachment!=null){
    throw new TypeError('Idea feedback cannot include attachments');
  }
  const bundle=feedbackKind===FeedbackKind.BUG
    ?validateFeedbackDiagnosticBundle(diagnosticBundle)
    :null;
  return Object.freeze({
    id:requestId(rawRequestId),
    requestId:requestId(rawRequestId),
    userId:requiredText(userId,'userId',128),
    kind:feedbackKind,
    description:requiredText(description,'description',4000),
    currentScreen:optionalText(currentScreen,120),
    currentModule:optionalText(currentModule,120),
    diagnosticBundle:bundle,
    attachment:validateFeedbackAttachment(attachment),
    createdAt:created,
    updatedAt:updated,
    deletedAt:null,
    syncStatus,
    syncAttempts:Math.max(0,Number(syncAttempts)||0),
    lastSyncAttemptAt:lastSyncAttemptAt?String(lastSyncAttemptAt):null,
    lastSyncFailureAt:lastSyncFailureAt?String(lastSyncFailureAt):null,
    lastSyncErrorCode:lastSyncErrorCode?String(lastSyncErrorCode).slice(0,80):null,
    nextSyncAttemptAt:nextSyncAttemptAt?String(nextSyncAttemptAt):null,
    deliveryStatus:String(deliveryStatus||FeedbackDeliveryStatus.PENDING)
  });
}

export function feedbackSubmissionToRemoteRow(submission){
  const value=createFeedbackSubmission(submission);
  return Object.freeze({
    user_id:value.userId,
    request_id:value.requestId,
    kind:value.kind,
    description:value.description,
    current_screen:value.currentScreen,
    current_module:value.currentModule,
    diagnostic_bundle:value.diagnosticBundle,
    attachment_path:value.attachment?.path??null,
    attachment_mime_type:value.attachment?.mimeType??null,
    attachment_size_bytes:value.attachment?.sizeBytes??null,
    submitted_at:value.createdAt
  });
}
